/**
 * ChapterFlow — 챕터 전진/점프·던전 보상 지급 흐름 (W5, Phaser-free).
 *
 * GameScene에 잔류하던 도메인 로직(전멸→다음 챕터 + 확정 지급, 치트 점프, 던전 보상 해석)을
 * 추출한다. 전장 재구성(러너)·컨트롤러 교체(번들 스왑)는 셸 콜백 — 결정/지급만 여기서.
 */
import { ChapterController } from "../systems/ChapterController.js";
import { DungeonController } from "../systems/DungeonController.js";
import { maxTabUnlockChapter } from "../ui/tabLock.js";
import { resolveRewards, type ResolvedReward } from "../systems/RewardResolver.js";
import { expandedModulePool } from "../data/moduleDefinitions.js";
import { diag } from "./diag.js";
import { AnalyticsEvent } from "../analytics/analyticsEvents.js";
import type { GameContext } from "./GameContext.js";
import { WORLD_CHAPTER_SPAN, worldStartChapterForChapter } from "../data/worldData.js";
import type { DialogueTrigger } from "../data/dialogueData.js";

const log = diag("chapter");

export interface ChapterFlowDeps {
  ctx: GameContext;
  /** 확정 지급 모듈 부여 (GridCommands.grantModule). */
  grantModule(definitionId: string): void;
  /** 통합 보상 지급 + 획득 모달 표시. */
  grantReward(reward: ResolvedReward): void;
  discovered(): ReadonlySet<string>;
  /** 챕터 전환 연출 — 게임뷰 오버레이 + 새 벽돌 페이드인 (러너). */
  transitionChapterGrid(): void;
  /** 필드 즉시 재구성 (치트 점프 — clearField+buildChapterGrid). */
  rebuildField(): void;
  /** ChapterController 교체 — 셸이 controllers 번들 스왑으로 반영. */
  replaceChapter(next: ChapterController): void;
  invalidateUi(): void;
  persist(): void;
  /** 챕터 잠금 탭 배지 갱신 — bestChapter 변동(전진/치트 점프) 시 해제된 탭의 자물쇠를 푼다. */
  refreshTabLocks(): void;
  /** 챕터 도달 알림 — 가이드퀘스트 등 진행 레일이 소비한다. */
  onChapterReached?(chapter: number): void;
  /** 정상 전투 진행에서 발생한 강제 다이어로그 trigger. 치트 점프는 이 경로를 타지 않는다. */
  forceDialogueTrigger?(trigger: DialogueTrigger): void;
}

export class ChapterFlow {
  constructor(private readonly deps: ChapterFlowDeps) {}

  /**
   * 전멸 → 다음 챕터. 확정 지급(지시문 §19): 챕터 3 → Electric, 5 → Explosion
   * (이미 발견했으면 중복 지급 없음). 모듈 UI는 챕터 클리어로 안 변하므로 전체 재구성 없음 —
   * HUD는 매 프레임, 지급 시엔 GridCommands 내부 무효화가 갱신한다.
   */
  nextChapter(): void {
    const s = this.deps.ctx.chapter().advanceChapter();
    log.diag("nextChapter", `chapter=${s}`);
    this.grantFixedRewards(s);
    this.deps.transitionChapterGrid();
    this.deps.ctx.analytics().log("chapter_clear", { chapter: s });
    // 환생 가능 조건 최초 충족 1회 퍼널 (funnel #10) — 정상 전진 경로만(치트 점프 setChapter 제외). markOnce dedup.
    if (
      this.deps.ctx.rebirth().canRebirth(s) &&
      this.deps.ctx.analyticsMilestones().markOnce(AnalyticsEvent.RebirthReady)
    ) {
      this.deps.ctx.analytics().log(AnalyticsEvent.RebirthReady);
    }
    this.deps.refreshTabLocks(); // bestChapter 증가 → 해제된 탭 잠금 풀기
    this.deps.onChapterReached?.(s);
    if (isWorldOneBossDestroyedNextChapter(s)) {
      this.deps.forceDialogueTrigger?.("w1BossDefeated");
    }
    this.deps.persist();
  }

  /** 치트 챕터 점프 — 컨트롤러 교체 + 확정 지급 소급 + 필드 재구성. */
  setChapter(chapter: number): void {
    const n = Math.max(1, Math.floor(chapter));
    this.deps.replaceChapter(
      new ChapterController(n, Math.max(n, this.deps.ctx.chapter().bestChapter)),
    );
    this.grantFixedRewards(n);
    this.deps.rebuildField();
    this.deps.invalidateUi();
    this.deps.refreshTabLocks(); // 치트 점프로 bestChapter 변동 → 잠금 갱신
    this.deps.onChapterReached?.(n);
    this.deps.persist();
  }

  /**
   * 치트: 모든 탭 잠금 해제 — bestChapter만 최대 해제 챕터로 올린다(currentChapter 유지).
   * setChapter와 달리 필드 재구성·모듈 소급 지급을 하지 않는다(탭 잠금만 갱신하는 경량 경로).
   */
  unlockAllTabs(): void {
    const current = this.deps.ctx.chapter().currentChapter;
    const best = Math.max(maxTabUnlockChapter(), this.deps.ctx.chapter().bestChapter);
    this.deps.replaceChapter(new ChapterController(current, best));
    this.deps.invalidateUi();
    this.deps.refreshTabLocks(); // bestChapter 상승 → 해제된 탭 잠금 풀기
    this.deps.persist();
  }

  /**
   * 일반 전투 패배 — 현재 월드의 1챕터로 되돌린다.
   * 환생 포인트 지급·골드/모듈 초기화는 하지 않아 수동 환생 보상 루프와 분리한다.
   */
  loseBattleToChapterOne(): void {
    const chapter = this.deps.ctx.chapter();
    chapter.resetToChapter(worldStartChapterForChapter(chapter.currentChapter));
    this.deps.rebuildField();
    this.deps.invalidateUi();
    this.deps.refreshTabLocks();
    this.deps.persist();
  }

  /** 던전 index 보상을 해석·지급 + 획득 모달 (Math.random 주입). */
  grantDungeonReward(index: number): void {
    const reward = resolveRewards(DungeonController.rewardForIndex(index), {
      rng: Math.random,
      rarityDefs: expandedModulePool(),
    });
    this.deps.grantReward(reward);
  }

  /** 챕터 도달 확정 지급 — nextChapter(정확히 그 챕터)와 setChapter(소급) 공용 (≥ 비교). */
  private grantFixedRewards(chapter: number): void {
    if (chapter >= 3 && !this.deps.discovered().has("electric")) this.deps.grantModule("electric");
    if (chapter >= 5 && !this.deps.discovered().has("explosion")) this.deps.grantModule("explosion");
  }
}

function isWorldOneBossDestroyedNextChapter(chapter: number): boolean {
  return chapter === WORLD_CHAPTER_SPAN + 1;
}
